import React, { memo } from "react";
import "./RateTable.scss";

/*
 * Date / Time / Rate / Amount breakdown for a set of selected pay request
 * dates. Web counterpart of the mobile TransactionalDetailsSection table;
 * mobile's swipe-to-deselect is a trailing remove button here.
 *
 * Rows use the mobile `PayRequestRow` shape:
 *   { date, label, hours, minutes, rate, amount }
 * Shift-threshold contracts add a `shifts` decimal quantity and the Quantity
 * column shows that instead of hours/minutes.
 */

const RemoveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const clampTimePart = (value, max) => {
  const digits = String(value).replace(/\D/g, "").slice(0, 2);
  if (digits === "") return "";
  return String(Math.min(Number(digits), max));
};

// Splits a "1.5" shift quantity into its whole and decimal parts for display
// as two boxes, mirroring hours:minutes — a value with no decimal part shows "00".
const splitShiftValue = (value) => {
  const [wholePart, decimalPart] = String(value ?? "").split(".");
  return {
    whole: wholePart || "0",
    decimal: decimalPart ? decimalPart.padEnd(2, "0").slice(0, 2) : "00",
  };
};

const RateTable = ({ rows = [], onTimeChange, onRemove, disabled = false, isShiftContract = false }) => {
  if (rows.length === 0) {
    return (
      <div className="rate-table rate-table--empty">
        Select one or more dates on the calendar to see the breakdown.
      </div>
    );
  }

  const handleTimeChange = (date, field, max) => (e) => {
    onTimeChange?.(date, field, clampTimePart(e.target.value, max));
  };

  return (
    <div className="rate-table">
      <div className="rate-table__head">
        <span className="rate-table__col rate-table__col--date">Date</span>
        <span className="rate-table__col rate-table__col--time">Quantity</span>
        <span className="rate-table__col rate-table__col--rate">Rate</span>
        <span className="rate-table__col rate-table__col--amount">Amount</span>
        <span className="rate-table__col rate-table__col--action" />
      </div>

      <div className="rate-table__body">
        {rows.map((row) => (
          <div key={row.date} className="rate-table__row">
            <span className="rate-table__col rate-table__col--date">{row.label}</span>

            <span className="rate-table__col rate-table__col--time">
              {isShiftContract ? (
                <div className="rate-table__shift-quantity">
                  <span className="rate-table__shift-inputs">
                    <input
                      type="text"
                      className="rate-table__time-input rate-table__time-input--shift"
                      value={splitShiftValue(row.shifts).whole}
                      disabled={true}
                      inputMode="numeric"
                      aria-label={`Shifts for ${row.label}`}
                    />
                    <span className="rate-table__time-colon">.</span>
                    <input
                      type="text"
                      className="rate-table__time-input rate-table__time-input--shift"
                      value={splitShiftValue(row.shifts).decimal}
                      disabled={true}
                      inputMode="numeric"
                      aria-label={`Shift decimal for ${row.label}`}
                    />
                  </span>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    className="rate-table__time-input"
                    value={row.hours}
                    onChange={handleTimeChange(row.date, "hours", 24)}
                    disabled={true}
                    inputMode="numeric"
                    aria-label={`Hours for ${row.label}`}
                  />
                  <span className="rate-table__time-colon">:</span>
                  <input
                    type="text"
                    className="rate-table__time-input"
                    value={row.minutes}
                    onChange={handleTimeChange(row.date, "minutes", 59)}
                    disabled={true}
                    inputMode="numeric"
                    aria-label={`Minutes for ${row.label}`}
                  />
                </>
              )}
            </span>

            <span className="rate-table__col rate-table__col--rate">
              $ {Number(row.rate ?? 0).toFixed(2)}
            </span>

            <span className="rate-table__col rate-table__col--amount">
              <span className="rate-table__amount-pill">
                $ {Number(row.amount ?? 0).toFixed(2)}
              </span>
            </span>

            <span className="rate-table__col rate-table__col--action">
              {!disabled && (
                <button
                  type="button"
                  className="rate-table__remove"
                  onClick={() => onRemove?.(row.date)}
                  aria-label={`Deselect ${row.label}`}
                >
                  <RemoveIcon />
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(RateTable);
